import { appStoreURL, faqEntries, howToSteps } from "../../../shared/website-seo.mjs";

type Navigate = (event: React.MouseEvent<HTMLAnchorElement>) => void;
type GuideProps = { navigate: Navigate };

// The visible questions and the questions published as structured data are the same array, so a
// search result can never quote an answer that is not on the page.
function Questions({ path }: { path: string }) {
  const entries = faqEntries.filter((entry) => entry.path === path);
  return <div className="qa-list">
    {entries.map(({ question, answer }) => <div className="qa-item" key={question}>
      <h3>{question}</h3>
      <p>{answer}</p>
    </div>)}
  </div>;
}

function GuideArticle({ eyebrow, title, intro, children }: React.PropsWithChildren<{ eyebrow: string; title: string; intro: string }>) {
  return <article className="page-width article">
    <p className="eyebrow">{eyebrow}</p>
    <h1>{title}</h1>
    <p className="article-intro">{intro}</p>
    {children}
  </article>;
}

function GuideLinks({ navigate: onNavigate, links }: { navigate: Navigate; links: [string, string][] }) {
  return <nav className="guide-links" aria-label="Keep reading">
    {links.map(([label, href]) => <a href={href} onClick={onNavigate} key={href}>{label}</a>)}
  </nav>;
}

export function BlockOnIPhonePage({ navigate: onNavigate }: GuideProps) {
  return <GuideArticle
    eyebrow="Guide"
    title="How to block porn on iPhone"
    intro="Screen Time is the right first layer. Here are the rest, and how to tell when one has quietly switched off.">
    <h2>Start with what Apple gives you</h2>
    <p>Screen Time can restrict adult websites on iPhone and iPad. Turn it on before anything else. It has limits: the iOS content filter only knows sites Apple has already categorized, and it does nothing about Reels and Shorts. On the day it gets switched off, nothing tells you.</p>
    <h2>Set it up in about ten minutes</h2>
    <ol className="step-list">
      {howToSteps.map(({ name, text }, index) => <li id={`step-${index + 1}`} key={name}>
        <h3>{name}</h3>
        <p>{text}</p>
      </li>)}
    </ol>
    <div className="article-actions"><a className="primary-button" href={appStoreURL}>Get ClearGuard for iPhone</a></div>
    <h2>What each layer covers</h2>
    <div className="comparison">
      <div className="feature">
        <h3>Websites in Safari and supported browsers</h3>
        <p>Known adult websites are blocked, along with any website you add yourself. A browser that will not accept the extension is blocked as a whole app instead.</p>
      </div>
      <div className="feature">
        <h3>Apps and feeds</h3>
        <p>Apps you pick through Apple's picker are blocked. Reels and Shorts links are blocked in the browser, and inside a native app iOS cannot separate one feed from the rest.</p>
      </div>
    </div>
    <h2>Questions people ask</h2>
    <Questions path="/block-porn-on-iphone" />
    <p className="disclosure">No blocker can promise that every page, app, browser, network, or newly created address is blocked. ClearGuard shows what it can check and the one step that repairs anything marked Needs Attention.</p>
    <GuideLinks navigate={onNavigate} links={[["Block Reels and Shorts", "/block-reels-and-shorts"], ["A blocker that does not watch your screen", "/private-porn-blocker"], ["What ClearGuard costs", "/pricing"]]} />
  </GuideArticle>;
}

export function ReelsAndShortsPage({ navigate: onNavigate }: GuideProps) {
  return <GuideArticle
    eyebrow="Guide"
    title="Block Reels and Shorts on iPhone"
    intro="One switch stops supported Reels and Shorts links from opening in your browser. What iOS will not let a blocker do inside an app is the part worth knowing.">
    <h2>One switch, not a project</h2>
    <p>Open Settings in ClearGuard and turn on Reels and Shorts. Supported short-form video links are blocked in Safari and in browsers that accept the ClearGuard extension. The rest of each site keeps working.</p>
    <h2>Native apps work differently</h2>
    <p>iOS does not let a blocker reach inside another app and remove one feed. If you pick Instagram, YouTube, or TikTok through Apple's picker, the whole app goes. ClearGuard tells you which one you are getting before you choose.</p>
    <div className="comparison">
      <div className="feature">
        <h3>In a browser</h3>
        <p>Supported Reels and Shorts links are blocked. Messages, search, and the rest of the website stay available.</p>
      </div>
      <div className="feature">
        <h3>In the app</h3>
        <p>The whole app is blocked. That is the only thing iOS offers here.</p>
      </div>
    </div>
    <h2>Add the rest of what pulls you off</h2>
    <p>Short-form video is rarely the only distraction on the phone. Any website you add is blocked alongside the built-in list, and a Protection Schedule in ClearGuard Pro holds those blocks through the hours you keep losing. Change something on one connected device and the others catch up.</p>
    <div className="article-actions"><a className="primary-button" href="/downloads" onClick={onNavigate}>Get ClearGuard</a></div>
    <h2>Questions people ask</h2>
    <Questions path="/block-reels-and-shorts" />
    <GuideLinks navigate={onNavigate} links={[["How to block porn on iPhone", "/block-porn-on-iphone"], ["How ClearGuard blocks", "/protection"], ["What ClearGuard costs", "/pricing"]]} />
  </GuideArticle>;
}

export function PrivateBlockerPage({ navigate: onNavigate }: GuideProps) {
  return <GuideArticle
    eyebrow="Guide"
    title="A blocker that does not watch your screen"
    intro="Plenty of blockers work by sending someone else a record of what you did. ClearGuard blocks on your device and leaves the record there.">
    <h2>What ClearGuard never collects</h2>
    <p>A content blocker does not have to track what you browse in order to block it. ClearGuard does not retain your browsing history, DNS queries, screenshots, private messages, PINs, or the app choices you make on a device. There is no screen recording and no feed of your activity for anyone to read.</p>
    <h2>You do not need an accountability partner</h2>
    <p>Most porn blockers are built around an accountability partner who gets a report of what you looked at. ClearGuard works with no account and no partner at all. If you do want someone involved, Pro connects one trusted partner, and what reaches them is small on purpose: that you asked for support, and whether your protection needs attention. Nothing about what you were doing.</p>
    <h2>What happens if you sync</h2>
    <p>ClearGuard Pro keeps your block list and Recovery data on the devices you connect. It is encrypted on your device before upload, and ClearGuard cannot read it. A new device needs approval from one you already use, or your printed recovery key, before it can open any of it.</p>
    <div className="comparison">
      <div className="feature">
        <h3>Stays on your device</h3>
        <p>Your browsing, your Protection PIN, and the apps you picked. None of it has anywhere else to go.</p>
      </div>
      <div className="feature">
        <h3>Encrypted before it leaves</h3>
        <p>Your synced block list, notes, and check-ins. Your devices hold the keys needed to open them.</p>
      </div>
    </div>
    <h2>Questions people ask</h2>
    <Questions path="/private-porn-blocker" />
    <p className="disclosure">One optional feature does send data out: if you turn on checks for unclear public pages, ClearGuard sends a public page address without search terms and a short, automatically redacted text sample. Authenticated pages, mail, messages, and private inboxes are excluded, and the sample is not stored.</p>
    <GuideLinks navigate={onNavigate} links={[["What ClearGuard stores", "/security"], ["Privacy policy", "/privacy"], ["How to block porn on iPhone", "/block-porn-on-iphone"]]} />
  </GuideArticle>;
}

export function UrgeHelpPage({ navigate: onNavigate }: GuideProps) {
  return <GuideArticle
    eyebrow="Guide"
    title="Free offline help for a hard moment"
    intro="Urge Help is free, works with no signal, and needs no account. It is there for the ten minutes that decide the evening.">
    <h2>What it does</h2>
    <div className="three-up">
      <div className="feature"><h3>Slows the moment down</h3><p>A short sequence to pause, breathe, and let the pull drop instead of arguing with it.</p></div>
      <div className="feature"><h3>Changes the setup</h3><p>Move rooms, put the phone down, or start Protection Lock for an hour so the decision is already made.</p></div>
      <div className="feature"><h3>Asks for backup</h3><p>With ClearGuard Pro you can tell your trusted partner you need support, without sending them anything you browsed.</p></div>
    </div>
    <h2>If you are trying to quit</h2>
    <p>Most people trying to quit porn end up looking for a panic button — one thing to press when it starts. Urge Help is that button. The blocking around it does the quieter work, because a decision you already made is easier to keep than one you have to make again at midnight.</p>
    <p>The 30-day Recovery lesson program is written but not shipped. It is waiting on independent professional review, and until that is recorded, what you get is Urge Help and the blocking.</p>
    <h2>Why blocking and urge help belong together</h2>
    <p>A blocker removes the easy route. It does not do much for the moment you are already looking for a way around it. ClearGuard keeps both in one app: the turn-off delay buys you 24 hours, and Urge Help is for the worst ten minutes of them.</p>
    <div className="article-actions"><a className="primary-button" href={appStoreURL}>Get Urge Help in ClearGuard</a></div>
    <h2>Questions people ask</h2>
    <Questions path="/urge-help" />
    <p className="disclosure">The 30-day Recovery program is still under professional review and is not included in the current release. ClearGuard is not therapy or medical treatment. If you are in crisis, contact a local emergency service or a qualified professional.</p>
    <GuideLinks navigate={onNavigate} links={[["Recovery in ClearGuard", "/recovery"], ["How to block porn on iPhone", "/block-porn-on-iphone"], ["What ClearGuard costs", "/pricing"]]} />
  </GuideArticle>;
}

export function QuestionsPage({ navigate: onNavigate }: GuideProps) {
  return <GuideArticle
    eyebrow="Questions"
    title="ClearGuard questions and answers"
    intro="What ClearGuard blocks, what it cannot block, what it stores, and what happens when you want to turn it off.">
    <Questions path="/faq" />
    <div className="article-actions"><a className="primary-button" href="/downloads" onClick={onNavigate}>Get ClearGuard</a></div>
    <GuideLinks navigate={onNavigate} links={[["How to block porn on iPhone", "/block-porn-on-iphone"], ["Block Reels and Shorts", "/block-reels-and-shorts"], ["Support", "/support"]]} />
  </GuideArticle>;
}
